import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ErrorMessageConstant } from '@utility/constants/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import type { StorageProviderDetailType } from 'types/storage-provider/detail';
import { providerKeys } from '../lib/query-keys';
import {
  isV2Response,
  type StorageProviderV2Detail,
  type StorageProviderV2InventoryResponse,
} from '../types-v2';

type DetailResponse =
  | { data: StorageProviderDetailType }
  | StorageProviderV2Detail;

/**
 * One provider. `v2` is the whole `source: 'v2'` answer, null for the legacy
 * one (or a failed load, which the legacy page shows), and undefined until
 * this id's first answer, so neither version renders under a new id early.
 */
export function useProvider(id?: string, enabled = true) {
  const queryClient = useQueryClient();
  const key = providerKeys.detail(id ?? '');
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      StorageProviderService.getById(id) as Promise<DetailResponse>,
    enabled: enabled && !!id,
  });

  const response = query.data;
  const v2 = response
    ? isV2Response<StorageProviderV2Detail>(response)
      ? response
      : null
    : query.isError
      ? null
      : undefined;
  const data =
    response && !isV2Response<StorageProviderV2Detail>(response)
      ? response.data
      : undefined;

  return {
    data,
    loading: query.isFetching,
    error: query.error?.message ?? '',
    /* A tab's save puts the provider it got back into the cache. */
    setData: (next: StorageProviderDetailType) =>
      queryClient.setQueryData<DetailResponse>(key, { data: next }),
    isFBA: [data?.integration_type, data?.type].includes('spapi'),
    refresh: async () => {
      await query.refetch();
    },
    v2,
  };
}

/** Total stock volume, for providers that hold stock. */
export function useProviderCbm(id: string | undefined, enabled: boolean) {
  const query = useQuery({
    queryKey: providerKeys.cbm(id ?? ''),
    queryFn: async () => {
      try {
        return (await StorageProviderService.cbm(id)) as {
          total_CBM?: number;
          total_CBF?: number;
        };
      } catch (error) {
        ToastMessage.error(
          (error as Error).message || ErrorMessageConstant.defaultAPIError,
        );
        throw error;
      }
    },
    enabled: enabled && !!id,
  });
  return query.data ?? null;
}

/** One page of a V2 provider's stock; nothing is fetched until `enabled`. */
export function useProviderInventoryV2(
  id: string | undefined,
  enabled: boolean,
  page: number,
  pageSize: number,
) {
  const query = useQuery({
    queryKey: providerKeys.inventoryV2(id ?? '', page, pageSize),
    queryFn: () =>
      StorageProviderService.inventoryV2(id, {
        page,
        pageSize,
      }) as Promise<StorageProviderV2InventoryResponse>,
    enabled: enabled && !!id,
    placeholderData: keepPreviousData,
  });
  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    loading: query.isFetching,
    error: query.error?.message ?? '',
  };
}
